/**
 * Counter-offer price sanity gate — shared by every surface that submits a
 * counter (QA sheet T-41).
 *
 * > "Need validation or reasonableness checks on the counter offer price. I
 * > typed in 65,500,000 and it should have stopped the counter offer if it was
 * > outside of 20% from the asking price for verification." — 7/28/26
 *
 * A stray digit turns $655,000 into $6,550,000 and nothing objects. The rule
 * lives here rather than in one modal because the app has two counter-offer
 * entry points — `OfferManagement`'s `CounterOfferModal` and the inline
 * `CounterOfferForm` in `NegotiationWorkspace` — and a guard on only one of
 * them is not a guard.
 *
 * The gate is a confirmation, not a hard block: a seller may legitimately
 * counter far above asking in a hot market, and the finding asked for
 * verification rather than rejection.
 */

/** Counters beyond +/- this fraction of the asking price need confirmation. */
export const COUNTER_PRICE_TOLERANCE = 0.2;

/**
 * Signed fraction a counter price sits away from the asking price, or null
 * when there is nothing meaningful to compare against.
 *
 * @param {number|string} counterPrice
 * @param {number|string|null|undefined} listPrice
 * @returns {number|null}
 */
export function priceDeviation(counterPrice, listPrice) {
  const cp = Number(counterPrice);
  const lp = Number(listPrice);
  if (!Number.isFinite(cp) || !Number.isFinite(lp) || lp <= 0 || cp <= 0) return null;
  return (cp - lp) / lp;
}

/**
 * Whether a counter price is far enough off asking to warrant confirmation.
 * Inert (false) when there is no asking price to compare against.
 *
 * @param {number|string} counterPrice
 * @param {number|string|null|undefined} listPrice
 * @returns {boolean}
 */
export function isCounterPriceOutOfRange(counterPrice, listPrice) {
  const deviation = priceDeviation(counterPrice, listPrice);
  return deviation !== null && Math.abs(deviation) > COUNTER_PRICE_TOLERANCE;
}
